/**
 * ラベル: 値 の一覧(antd Descriptions の置き換え)。
 *
 * **未設定の項目は既定で畳む**。「(未設定)」が 10 行並ぶと、設定されている
 * 値が埋もれる。畳んだ数を「未設定の項目 N 件を表示」で出し、押せば全部出す。
 * 何を未設定と見るかは `unsetWhen` で上書きできる(「0 / 0」など)。
 */
import { Button, Typography } from "antd";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { isUnset } from "./unset";

export type KvItem = {
  label: string;
  /** null / undefined / 空文字 / false は「未設定」 */
  value?: ReactNode;
  /** 行をまたいで広く出す(備考など) */
  wide?: boolean;
  /** 未設定でも畳まない(在籍状態など、無いこと自体が情報の項目) */
  always?: boolean;
  /** 値をリンクにする */
  href?: string;
  /** 値の横にコピーボタン(シリアル・メールなど)。value が文字のときだけ効く */
  copyable?: boolean;
};

export function KeyValueList({
  items,
  columns = 1,
  narrow = false,
  unsetLabel = "未設定",
  unsetWhen = isUnset,
}: {
  items: KvItem[];
  columns?: 1 | 2 | 3;
  /** ラベル列を細く(右パネル向け) */
  narrow?: boolean;
  /** 未設定の値の表示(「未設定」「なし」など)。畳みリンクの文言には使わない */
  unsetLabel?: string;
  /** 未設定の判定を差し替える */
  unsetWhen?: (value: ReactNode) => boolean;
}) {
  const [showUnset, setShowUnset] = useState(false);
  const hidden = items.filter((it) => unsetWhen(it.value) && !it.always);
  const shown = showUnset ? items : items.filter((it) => !unsetWhen(it.value) || it.always);
  const cls = [
    "ui-kv",
    columns === 2 ? "ui-kv--2" : columns === 3 ? "ui-kv--3" : "",
    narrow ? "ui-kv--narrow" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const render = (it: KvItem) => {
    if (it.copyable && typeof it.value === "string") {
      return <Typography.Text copyable={{ text: it.value }}>{it.value}</Typography.Text>;
    }
    if (it.href) return <Link to={it.href}>{it.value}</Link>;
    return it.value;
  };
  return (
    <dl className={cls}>
      {shown.map((it) => {
        const unset = unsetWhen(it.value);
        return [
          <dt key={`${it.label}-k`}>{it.label}</dt>,
          <dd
            key={`${it.label}-v`}
            className={[unset ? "is-unset" : "", it.wide ? "is-wide" : ""].filter(Boolean).join(" ")}
          >
            {unset ? unsetLabel : render(it)}
          </dd>,
        ];
      })}
      {hidden.length > 0 && (
        <div className="ui-kv-more">
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowUnset((v) => !v)}>
            {showUnset ? "未設定の項目を隠す" : `未設定の項目 ${hidden.length} 件を表示`}
          </Button>
        </div>
      )}
    </dl>
  );
}
